import { compact, isEmpty, keys } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  DEFAULT_PHRASES,
  DEFAULT_RULES,
  SAMPLE_MIN_WORD_COUNT_BULK_TAGGING,
} from '~/components/AutomationHub/constants';
import { trackSegment } from '~/components/SegmentAnalytics';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { AutomationStageType } from '~/enums/AutomationStageType';
import { QueryParamType } from '~/enums/QueryParamType';
import { SearchQueryFormatTypes } from '~/enums/SearchQueryFormatTypes';
import { TrainingStage } from '~/enums/TrainingStage';
import { TrainingStatus } from '~/enums/TrainingStatus';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useIntl } from 'react-intl';
import { buildQuery } from '~/redux/api/methods/search';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import { ERROR, SUCCESS } from '~/types/toast.types';
import { getPageSearchQueryByKey } from '~/utils/searchQuery';
import { countWords } from '~/utils/strings';
import { getClientInfo, getUserClientInfo } from '~/utils/user';

import { calculateF1Score } from '../util';
import AutomationTypes from './Content/AutomationTypes';
import GuidedAiForm from './Content/GuidedAiForm';
import GuidedAiReview from './Content/GuidedAiReview';
import GuidedAiStatus from './Content/GuidedAiStatus';
import QuickAiForm from './Content/QuickAiForm';

// Add this import

const AutomationHubManagement = () => {
  const intl = useIntl(); // Add this line
  const { navigate } = useRouting();
  const dispatch = useDispatch();

  const retrainText = intl.formatMessage({
    id: 'AutomationHubManagement.retrainButton',
    defaultMessage: 'Retrain',
    description: 'Primary button text for retraining model',
  });
  const publishText = intl.formatMessage({
    id: 'AutomationHubManagement.publishButton',
    defaultMessage: 'Publish',
    description: 'Button text for publishing model',
  });

  const createText = intl.formatMessage({
    id: 'AutomationHubManagement.createPredictionButton',
    defaultMessage: 'Create',
    description: 'Primary button text for creating a model',
  });

  const [evaluate, setEvaluate] = useState(false);
  const [retrainGuidedAIConfirmation, setRetrainGuidedAIConfirmation] =
    useState('');

  const currentUser = useCurrentUser();
  const model = useSelector(slices.automation.selectors.selectAutomationModel);

  const filters = useSelector(
    slices.automation.selectors.selectAutomationFilters,
  );
  const fields = useSelector(
    slices.automation.selectors.selectAutomationFields,
  );

  const manage = useSelector(
    slices.automation.selectors.selectAutomationManage,
  );

  const quickAiForm = useSelector(
    slices.automation.selectors.selectAutomationQuickAiForm,
  );
  const guidedAiForm = useSelector(
    slices.automation.selectors.selectAutomationGuidedAiForm,
  );
  const clause = useSelector(
    slices.automation.selectors.selectAutomationClause,
  );

  const [createSample, createTaggingResult] =
    api.endpoints.createSample.useMutation();
  const {
    error: createTaggingError,
    isError: isCreateTaggingError,
    isSuccess: isCreateTaggingSuccess,
    isLoading: isCreateTaggingLoading,
    data: createTaggingData,
  } = createTaggingResult;

  const [updateSample, updateTaggingResult] =
    api.endpoints.updateSample.useMutation();
  const {
    error: updateTaggingError,
    isError: isUpdateTaggingError,
    isSuccess: isUpdateTaggingSuccess,
    isLoading: isUpdateTaggingLoading,
  } = updateTaggingResult;

  const [createTraining, createTrainingResult] =
    api.endpoints.createTraining.useMutation();
  const {
    isError: isCreateTrainingError,
    isSuccess: isCreateTrainingSuccess,
    originalArgs: createTrainingOriginalArgs,
    data: createTrainingData,
    error: createTrainingError,
    isLoading: isCreateTrainingLoading,
  } = createTrainingResult;

  const [updateTrainingLabels, updateTrainingLabelsResult] =
    api.endpoints.updateTrainingLabels.useMutation();
  const {
    isError: isUpdateTrainingLabelsError,
    isSuccess: isUpdateTrainingLabelsSuccess,
    error: updateTrainingLabelsError,
    isLoading: isUpdateTrainingLabelsLoading,
  } = updateTrainingLabelsResult;

  const [createRetraining, createRetrainingResult] =
    api.endpoints.createRetraining.useMutation();
  const {
    isError: isCreateRetrainingError,
    isSuccess: isCreateRetrainingSuccess,
    error: createRetrainingError,
    isLoading: isCreateRetrainingLoading,
  } = createRetrainingResult;

  const [publishTrainedModel, publishTrainedModelResult] =
    api.endpoints.publishTrainedModel.useMutation();
  const {
    isError: isPublishTrainedModelError,
    isSuccess: isPublishTrainedModelSuccess,
    error: publishTrainedModelError,
    isLoading: isPublishTrainedModelLoading,
  } = publishTrainedModelResult;

  const [updateDescription] = api.endpoints.updateDescription.useMutation();

  // quickAiForm validation
  const isMissingQuickAiBasicInfo = !(
    quickAiForm.name &&
    quickAiForm.sampleName &&
    quickAiForm.content
  );
  const isSampleWordsInsufficient =
    countWords(quickAiForm.content) < SAMPLE_MIN_WORD_COUNT_BULK_TAGGING;

  const isTaggingClauseNameOverLimit = (quickAiForm.name?.length ?? 0) > 500;
  const isSampleNameOverLimit = (quickAiForm.sampleName?.length ?? 0) > 100;
  const doesQuickAiFormHaveErrors = (quickAiForm.errors?.length ?? 0) > 0;
  const isBasicTaggingInfoInvalid =
    isTaggingClauseNameOverLimit ||
    isSampleNameOverLimit ||
    doesQuickAiFormHaveErrors;

  // guidedAiForm validation
  const isBasicModelInfoMissing = !guidedAiForm.name || !guidedAiForm.uploadId;

  const isSmartClauseNameOverLimit = guidedAiForm.name.trimLeft().length > 500;
  const isClauseDescriptionOverLimit =
    (guidedAiForm.description?.length ?? 0) > 500;
  const doesGuidedAiFormHaveErrors = (guidedAiForm.errors?.length ?? 0) > 0;
  const isBasicModelInfoInvalid =
    isSmartClauseNameOverLimit ||
    isClauseDescriptionOverLimit ||
    doesGuidedAiFormHaveErrors;

  const isUpdating = !!quickAiForm.id;

  const isOnClausePage = !isEmpty(
    getPageSearchQueryByKey(QueryParamType.Clause, null),
  );

  const isExistingClauseOnClausePage =
    isOnClausePage && quickAiForm.doesClauseExist;

  const allSamples = guidedAiForm.samples || [];
  const availableSampleCount = allSamples.filter((s) => !!s.Text).length;
  const labeledSampleCount = allSamples.filter(
    (s) => !!s.Text && !!s.Label,
  ).length;
  const isAllSampleLabeled = labeledSampleCount === availableSampleCount;

  const isAccuracyOverGoal =
    calculateF1Score(model?.current?.accuracyMetrics) >= 0.9;
  const pastIterTimes = keys(model?.current?.pastIterations)?.length;
  const hasReachedRetrainingLimit = pastIterTimes >= 3;
  const isOnFirstTimeTraining = pastIterTimes <= 0;

  const isFirstTimeTrainingIdeal = isAccuracyOverGoal && isOnFirstTimeTraining;
  const isRetrainingNotIdeal = !isOnFirstTimeTraining && !isAccuracyOverGoal;

  const shouldShowPrimaryRetrain =
    (isOnFirstTimeTraining || isRetrainingNotIdeal) &&
    !hasReachedRetrainingLimit;

  const shouldShowSecondaryRetrain =
    !isOnFirstTimeTraining && isAccuracyOverGoal && !hasReachedRetrainingLimit;
  const shouldShowSecondaryPublish =
    (isFirstTimeTrainingIdeal || isRetrainingNotIdeal) &&
    !hasReachedRetrainingLimit;

  const onModelSubmit = async () => {
    setRetrainGuidedAIConfirmation('');

    const filterQuery = buildQuery({
      filters: filters.filter((f) => !!f.operatorId),
      fields,
      booleanQuery: '',
    });

    if (!!model.id) {
      updateDescription({
        clauseId: model.id,
        description: guidedAiForm.description,
      });
      return createTraining({
        params: {
          provisionName: guidedAiForm.name.trim(),
          description: guidedAiForm.description,
          trainingDataID: guidedAiForm.uploadId,
          filter: filterQuery,
          queryFormat: SearchQueryFormatTypes.V2,
        },
        clauseId: model.id,
      });
    } else {
      return createTraining({
        params: {
          provisionName: guidedAiForm.name.trim(),
          description: guidedAiForm.description,
          trainingDataID: guidedAiForm.uploadId,
          filter: filterQuery,
          queryFormat: SearchQueryFormatTypes.V2,
        },
      });
    }
  };

  const onHideModal = () => {
    dispatch(slices.automation.actions.resetAutomationQuickAiDataForm());
    dispatch(slices.automation.actions.resetAutomationGuidedAiDataForm());

    dispatch(
      slices.automation.actions.setAutomationModel({
        ...model,
        stage: TrainingStage.Empty,
      }),
    );
    if (!isOnClausePage)
      dispatch(
        slices.automation.actions.setAutomationClause({
          ...clause,
          name: '',
        }),
      );
    dispatch(
      slices.automation.actions.setAutomationManageStage(
        AutomationStageType.None,
      ),
    );
  };

  const onHideViewModal = () => {
    dispatch(
      slices.automation.actions.setAutomationManageStage(
        AutomationStageType.None,
      ),
    );
  };

  const onCloseGuidedAI = () => {
    if (
      manage === AutomationStageType.GuidedAiForm &&
      (guidedAiForm.name || guidedAiForm.description || guidedAiForm.uploadId)
    ) {
      showCancelGuidedAIWarning();
      return;
    }
    dispatch(
      slices.automation.actions.setAutomationManageStage(
        AutomationStageType.None,
      ),
    );
  };

  const onCloseGuidedAiReview = async () => {
    setEvaluate(false);

    const samples = guidedAiForm.samples.filter((s) => !!s.Text && !!s.Label);

    if (samples.length <= 0) {
      dispatch(
        slices.automation.actions.setAutomationManageStage(
          AutomationStageType.None,
        ),
      );
      return;
    }

    await updateTrainingLabels({
      samples,
      clauseId: guidedAiForm.id,
      metadata: guidedAiForm.metadata,
      evaluate: false,
    });
  };

  const onClose = {
    [AutomationStageType.GuidedAiForm]: onCloseGuidedAI,
    [AutomationStageType.QuickAiForm]: onHideModal,
    [AutomationStageType.AutomationTypes]: onHideModal,
    [AutomationStageType.GuidedAiReview]: onCloseGuidedAiReview,
    [AutomationStageType.GuidedAiStatus]: onHideModal,
    [AutomationStageType.ViewStatus]: onHideViewModal,
  };

  const postTrainingSubmit = () => {
    const { id, name, version } = createTrainingData;
    const { description } = guidedAiForm;

    const isRetraining = !!createTrainingOriginalArgs.clauseId;

    dispatch(
      slices.automation.actions.setAutomationModel({
        id,
        version,
        name,
        description,
        showCreating: true,
        stage: isRetraining ? TrainingStage.Empty : model.stage,
      }),
    );
    dispatch(
      slices.automation.actions.setAutomationManageStage(
        AutomationStageType.None,
      ),
    );
    dispatch(slices.automation.actions.resetAutomationFilters());

    const clauseNameParam = `${QueryParamType.Clause}=${encodeURIComponent(
      name,
    )}`;
    const clauseParam = `${QueryParamType.clauseId}=${id}`;

    navigate(`/automation/detail?&${clauseNameParam}&${clauseParam}`);
  };

  const postSubmit = {
    [AutomationStageType.GuidedAiForm]: postTrainingSubmit,
    [AutomationStageType.QuickAiForm]: onHideModal,
    [AutomationStageType.AutomationTypes]: onHideModal,
    [AutomationStageType.GuidedAiReview]: onHideModal,
    [AutomationStageType.GuidedAiStatus]: onHideModal,
    [AutomationStageType.ViewStatus]: onHideViewModal,
  };

  const onPublish = async () => {
    setRetrainGuidedAIConfirmation('');
    dispatch(
      slices.automation.actions.setAutomationModel({
        ...model,
        stage: TrainingStage.Empty,
      }),
    );
    return publishTrainedModel({ clauseId: guidedAiForm.id });
  };

  const [
    cancelGuidedAIWarning,
    showCancelGuidedAIWarning,
    hideCancelGuidedAIWarning,
  ] = useModal({
    title: intl.formatMessage(
      {
        id: 'AutomationHubManagement.cancelGuidedAiTitle',
        defaultMessage: 'Cancel Guided AI{clauseNamePart}?',
        description:
          'Title for cancel guided AI confirmation modal. clauseNamePart is ": {name}" if name exists, otherwise empty.',
      },
      {
        clauseNamePart: guidedAiForm.name ? `: ${guidedAiForm.name}` : '',
      },
    ),
    children: intl.formatMessage({
      id: 'AutomationHubManagement.cancelGuidedAiContent',
      defaultMessage:
        'Are you sure you want to cancel creating this custom clause?',
      description: 'Content for cancel guided AI confirmation modal',
    }),
    primaryAction: {
      text: intl.formatMessage({
        id: 'AutomationHubManagement.cancelButton',
        defaultMessage: 'Cancel',
        description: 'Button text for cancel guided AI confirmation modal',
      }),
      onClick: async () => {
        hideCancelGuidedAIWarning();
        dispatch(slices.automation.actions.resetAutomationGuidedAiDataForm());
        dispatch(
          slices.automation.actions.setAutomationManageStage(
            AutomationStageType.None,
          ),
        );
      },
    },
    cancelText: intl.formatMessage({
      id: 'AutomationHubManagement.closeButton',
      defaultMessage: 'Close',
      description:
        'Button text for closing cancel guided AI confirmation modal',
    }),
  });

  const [
    retrainGuidedAIWarning,
    showRetrainGuidedAIWarning,
    hideRetrainGuidedAIWarning,
  ] = useModal({
    title: intl.formatMessage({
      id: 'AutomationHubManagement.publishAndOverwriteTitle',
      defaultMessage: 'Publish and Overwrite',
      description: 'Title for publish and overwrite confirmation modal',
    }),
    children: (
      <Layout direction="column" spacing={4}>
        <Text>
          {intl.formatMessage(
            {
              id: 'AutomationHubManagement.publishAndOverwriteDescription',
              defaultMessage:
                "Once published, this improved model will identify new matches to the {clauseName} Clause, and it will overwrite the previous results. You won't be able to revert this action.",
              description:
                'Description for publish and overwrite confirmation modal. clauseName is the name of the clause. Example: clauseName = "Assignment"',
            },
            { clauseName: model?.name },
          )}
        </Text>
        <Text>
          {intl.formatMessage({
            id: 'AutomationHubManagement.publishAndOverwritePrompt',
            defaultMessage:
              'If you are sure, please type "OVERWRITE" in the box below',
            description: 'Prompt for publish and overwrite confirmation modal',
          })}
        </Text>
        <TextInput
          name="confirm-retrain"
          value={retrainGuidedAIConfirmation}
          onChange={setRetrainGuidedAIConfirmation}
        />
      </Layout>
    ),
    primaryAction: {
      text: intl.formatMessage({
        id: 'AutomationHubManagement.publishAndOverwriteButton',
        defaultMessage: 'Publish and Overwrite',
        description: 'Button text for publish and overwrite confirmation modal',
      }),
      onClick: () => {
        hideRetrainGuidedAIWarning();
        onPublish();
      },
      variant: 'danger',
      disabled: retrainGuidedAIConfirmation !== 'OVERWRITE',
    },
    onCancel: () => {
      setRetrainGuidedAIConfirmation('');
    },
  });

  const onError = ({ response }) => {
    const error = response?.data?.error ?? [];

    dispatch(
      slices.automation.actions.setAutomationQuickAiDataForm({
        ...quickAiForm,
        errors: Array.isArray(error) ? error : [error],
      }),
    );
    dispatch(
      slices.automation.actions.setAutomationGuidedAiDataForm({
        ...guidedAiForm,
        errors: Array.isArray(error) ? error : [error],
      }),
    );
  };

  useEffect(() => {
    if (isCreateTaggingSuccess) {
      const message =
        isOnClausePage && !quickAiForm.doesClauseExist
          ? intl.formatMessage(
              {
                id: 'AutomationHubManagement.newSampleAdded',
                defaultMessage: 'New Sample added to {clauseName} Clause',
                description:
                  'Toast message when new sample is added to a clause',
              },
              { clauseName: quickAiForm.name },
            )
          : intl.formatMessage(
              {
                id: 'AutomationHubManagement.clauseCreatedSuccess',
                defaultMessage: '{clauseName} Clause created successfully.',
                description:
                  'Toast message when clause is created successfully',
              },
              { clauseName: quickAiForm.name },
            );

      trackSegment('User creates new Quick AI Clause', {
        groupId: getClientInfo(currentUser),
        userId: getUserClientInfo(currentUser),
        sourceId: createTaggingData.source_id,
      });
      showToast(SUCCESS, message);
      postSubmit[manage]();
    }
    if (isCreateTaggingError) {
      const message =
        isOnClausePage && !quickAiForm.doesClauseExist
          ? intl.formatMessage(
              {
                id: 'AutomationHubManagement.createSampleFailed',
                defaultMessage:
                  'Create Sample in {clauseName} Clause failed. Please try again.',
                description: 'Toast message when creating a sample fails',
              },
              { clauseName: quickAiForm.name },
            )
          : intl.formatMessage(
              {
                id: 'AutomationHubManagement.createClauseFailed',
                defaultMessage:
                  'Create {clauseName} Clause failed. Please try again.',
                description: 'Toast message when creating a clause fails',
              },
              { clauseName: quickAiForm.name },
            );

      showToast(ERROR, message);
      onError(createTaggingError);
    }
  }, [isCreateTaggingSuccess, isCreateTaggingError]);

  useEffect(() => {
    if (isUpdateTaggingSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'AutomationHubManagement.sampleUpdatedSuccess',
          defaultMessage: 'Sample updated successfully.',
          description: 'Toast message when sample is updated successfully',
        }),
      );
      postSubmit[manage]();
    }
    if (isUpdateTaggingError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'AutomationHubManagement.updateSampleFailed',
          defaultMessage: 'Update Sample failed. Please try again.',
          description: 'Toast message when updating a sample fails',
        }),
      );
      onError(updateTaggingError);
    }
  }, [isUpdateTaggingSuccess, isUpdateTaggingError]);

  useEffect(() => {
    if (isCreateTrainingSuccess) {
      const message = intl.formatMessage(
        {
          id: 'AutomationHubManagement.clauseCreatedSuccessTraining',
          defaultMessage: '{clauseName} Clause created successfully.',
          description:
            'Toast message when clause is created successfully via training',
        },
        { clauseName: createTrainingData.name },
      );
      showToast(SUCCESS, message);
      postSubmit[manage]();
    }
    if (isCreateTrainingError) {
      const message = intl.formatMessage(
        {
          id: 'AutomationHubManagement.createModelFailed',
          defaultMessage: 'Create {modelName} Model failed. Please try again.',
          description: 'Toast message when creating a model fails',
        },
        { modelName: guidedAiForm.name },
      );

      showToast(ERROR, message);
      onError(createTrainingError);
    }
  }, [isCreateTrainingSuccess, isCreateTrainingError]);

  useEffect(() => {
    if (isUpdateTrainingLabelsSuccess) {
      const message = intl.formatMessage(
        {
          id: 'AutomationHubManagement.labelsUpdatedSuccess',
          defaultMessage: 'Labels of {modelName} updated successfully.',
          description: 'Toast message when labels are updated successfully',
        },
        { modelName: guidedAiForm.name },
      );
      showToast(SUCCESS, message);
      if (evaluate) {
        dispatch(
          slices.automation.actions.setAutomationManageStage(
            AutomationStageType.GuidedAiStatus,
          ),
        );
      } else {
        dispatch(
          slices.automation.actions.setAutomationModel({
            ...model,
            stage: TrainingStage.Waiting,
          }),
        );
        dispatch(
          slices.automation.actions.setAutomationManageStage(
            AutomationStageType.None,
          ),
        );
      }
    }
    if (isUpdateTrainingLabelsError) {
      const message = intl.formatMessage(
        {
          id: 'AutomationHubManagement.labelsUpdateFailed',
          defaultMessage:
            'Labels of {modelName} failed to update. Please try again.',
          description: 'Toast message when labels fail to update',
        },
        { modelName: guidedAiForm.name },
      );

      showToast(ERROR, message);
      onError(updateTrainingLabelsError);
    }
  }, [isUpdateTrainingLabelsSuccess, isUpdateTrainingLabelsError]);

  useEffect(() => {
    if (isCreateRetrainingSuccess) {
      const message = intl.formatMessage(
        {
          id: 'AutomationHubManagement.retrainSuccess',
          defaultMessage: 'Retrain {modelName} successfully.',
          description: 'Toast message when retraining is successful',
        },
        { modelName: guidedAiForm.name },
      );
      showToast(SUCCESS, message);
      postSubmit[manage]();
    }
    if (isCreateRetrainingError) {
      const message = intl.formatMessage(
        {
          id: 'AutomationHubManagement.retrainFailed',
          defaultMessage: 'Retrain {modelName} failed. Please try again.',
          description: 'Toast message when retraining fails',
        },
        { modelName: guidedAiForm.name },
      );

      showToast(ERROR, message);
      onError(createRetrainingError);
    }
  }, [isCreateRetrainingSuccess, isCreateRetrainingError]);

  useEffect(() => {
    if (isPublishTrainedModelSuccess) {
      const message = intl.formatMessage(
        {
          id: 'AutomationHubManagement.publishModelSuccess',
          defaultMessage: 'Publish model {modelName} successfully.',
          description: 'Toast message when publishing model is successful',
        },
        { modelName: guidedAiForm.name },
      );
      showToast(SUCCESS, message);
      postSubmit[manage]();
    }
    if (isPublishTrainedModelError) {
      const message = intl.formatMessage(
        {
          id: 'AutomationHubManagement.publishModelFailed',
          defaultMessage: 'Publish model {modelName} failed. Please try again.',
          description: 'Toast message when publishing model fails',
        },
        { modelName: guidedAiForm.name },
      );

      showToast(ERROR, message);
      onError(publishTrainedModelError);
    }
  }, [isPublishTrainedModelSuccess, isPublishTrainedModelError]);

  if (manage === AutomationStageType.None) return null;

  const onTaggingSubmit = async () => {
    if (isUpdating) {
      const { id, sampleName, content } = quickAiForm;
      return updateSample({ id, sampleName, content });
    } else {
      const { name, sampleName, content, applyTo, query } = quickAiForm;

      return createSample({
        name,
        sampleName,
        content,
        applyTo,
        query,
      });
    }
  };

  const onUpdateTrainingLabels = async () => {
    if (allSamples.length > availableSampleCount) {
      onCloseGuidedAiReview();
      return;
    }
    setEvaluate(true);

    const samples = guidedAiForm.samples.filter((s) => !!s.Text && !!s.Label);

    return updateTrainingLabels({
      clauseId: guidedAiForm.id,
      samples,
      metadata: guidedAiForm.metadata,
      evaluate: true,
    });
  };

  // iteration, not a proper retrain which is done from createTraining endpoint
  const onRetrain = async () => {
    dispatch(
      slices.automation.actions.setAutomationModel({
        ...model,
        stage: TrainingStage.Empty,
      }),
    );
    const rules = model.current.userInput?.rules ?? DEFAULT_RULES;
    const phrases = model.current.userInput?.phrases ?? DEFAULT_PHRASES;

    return createRetraining({
      clauseId: guidedAiForm.id,
      config: { rules, phrases },
    });
  };

  const handlePublishRetrain = () => {
    showRetrainGuidedAIWarning();
  };

  const title = {
    [AutomationStageType.AutomationTypes]: intl.formatMessage({
      id: 'AutomationHubManagement.getStartedTitle',
      defaultMessage: 'Get started',
      description: 'Title for automation types modal',
    }),
    [AutomationStageType.QuickAiForm]: intl.formatMessage(
      {
        id: 'AutomationHubManagement.quickAiFormTitle',
        defaultMessage: 'Create a New {type}',
        description:
          'Title for Quick AI form modal. type is either "Sample" or "Clause". Example: type = "Sample"',
      },
      { type: isOnClausePage ? 'Sample' : 'Clause' },
    ),
    [AutomationStageType.GuidedAiForm]: intl.formatMessage({
      id: 'AutomationHubManagement.guidedAiFormTitle',
      defaultMessage: 'Create a Guided AI',
      description: 'Title for Guided AI form modal',
    }),
    [AutomationStageType.GuidedAiReview]: intl.formatMessage(
      {
        id: 'AutomationHubManagement.guidedAiReviewTitle',
        defaultMessage: '{modelName} - Give Feedback on Samples',
        description:
          'Title for Guided AI review modal. modelName is the name of the model. Example: modelName = "Assignment"',
      },
      { modelName: model.name },
    ),
    [AutomationStageType.GuidedAiStatus]: intl.formatMessage({
      id: 'AutomationHubManagement.modelStatusTitle',
      defaultMessage: 'Model Status',
      description: 'Title for Guided AI status modal',
    }),
  };

  const predicted = model.stage === TrainingStage.Predicted;
  const shouldShowRetrainWarning = model.allVersions.length > 1;

  const content = {
    [AutomationStageType.AutomationTypes]: {
      disableFooter: true,
      cancelText: undefined,
      primary: null,
      secondary: [],
      component: AutomationTypes,
    },
    [AutomationStageType.QuickAiForm]: {
      disableFooter: false,
      cancelText: undefined,
      primary: {
        isLoading: isUpdating ? isUpdateTaggingLoading : isCreateTaggingLoading,
        disabled:
          isMissingQuickAiBasicInfo ||
          isSampleWordsInsufficient ||
          isBasicTaggingInfoInvalid ||
          isExistingClauseOnClausePage,
        text: isUpdating
          ? intl.formatMessage({
              id: 'AutomationHubManagement.editButton',
              defaultMessage: 'Edit',
              description: 'Primary button text for editing a sample',
            })
          : createText,
        onClick: onTaggingSubmit,
      },
      secondary: [],
      component: QuickAiForm,
    },
    [AutomationStageType.GuidedAiForm]: {
      disableFooter: false,
      cancelText: undefined,
      primary: {
        isLoading: isCreateTrainingLoading,
        disabled:
          isBasicModelInfoMissing ||
          isBasicModelInfoInvalid ||
          guidedAiForm.invalids,
        text: predicted
          ? intl.formatMessage({
              id: 'AutomationHubManagement.startRetrainingButton',
              defaultMessage: 'Start Retraining',
              description: 'Primary button text for starting retraining',
            })
          : createText,
        onClick: onModelSubmit,
      },
      secondary: [],
      component: GuidedAiForm,
    },
    [AutomationStageType.GuidedAiReview]: {
      disableFooter: false,
      cancelText: intl.formatMessage({
        id: 'AutomationHubManagement.finishGuidedAiReviewLater',
        defaultMessage: 'I will finish later',
        description: 'Cancel button text for guided AI review modal',
      }),
      primary: {
        isLoading: isUpdateTrainingLabelsLoading && isAllSampleLabeled,
        disabled: !isAllSampleLabeled,
        text:
          allSamples.length > availableSampleCount
            ? intl.formatMessage(
                {
                  id: 'AutomationHubManagement.guidedAiReviewPrimaryButton',
                  defaultMessage:
                    'Save {labeledSampleCount}/{availableSampleCount}',
                  description:
                    'Primary button text for guided AI review modal when sample count is more than available',
                },
                {
                  labeledSampleCount,
                  availableSampleCount,
                },
              )
            : intl.formatMessage(
                {
                  id: 'AutomationHubManagement.guidedAiReviewPrimaryButtonNoCount',
                  defaultMessage:
                    'Done {labeledSampleCount}/{availableSampleCount}',
                  description:
                    'Primary button text for guided AI review modal when sample count is more than available',
                },
                {
                  labeledSampleCount,
                  availableSampleCount,
                },
              ),
        onClick: onUpdateTrainingLabels,
      },
      secondary: [],
      component: GuidedAiReview,
    },
    [AutomationStageType.GuidedAiStatus]: {
      disableFooter: isEmpty(model?.current?.accuracyMetrics),
      cancelText: intl.formatMessage({
        id: 'AutomationHubManagement.finishGuidedAiStatusLater',
        defaultMessage: 'Finish Later',
        description: 'Cancel button text for guided AI status modal',
      }),
      primary: {
        isLoading: shouldShowPrimaryRetrain
          ? isCreateRetrainingLoading
          : isPublishTrainedModelLoading,
        disabled: model?.current?.status !== TrainingStatus.AWAITING_DECISION,
        text: shouldShowPrimaryRetrain ? retrainText : publishText,
        onClick: shouldShowPrimaryRetrain
          ? onRetrain
          : shouldShowRetrainWarning
            ? handlePublishRetrain
            : onPublish,
      },
      secondary: compact([
        shouldShowSecondaryPublish && {
          isLoading: isPublishTrainedModelLoading,
          disabled: model?.current?.status !== TrainingStatus.AWAITING_DECISION,
          text: publishText,
          onClick: shouldShowRetrainWarning ? handlePublishRetrain : onPublish,
        },
        shouldShowSecondaryRetrain && {
          isLoading: isCreateRetrainingLoading,
          disabled: model?.current?.status !== TrainingStatus.AWAITING_DECISION,
          text: retrainText,
          onClick: onRetrain,
        },
      ]),
      component: GuidedAiStatus,
    },
    [AutomationStageType.ViewStatus]: {
      disableFooter: true,
      cancelText: undefined,
      primary: null,
      secondary: [],
      component: GuidedAiStatus,
    },
  };

  const {
    component: Content,
    cancelText,
    disableFooter,
    primary,
    secondary,
  } = content[manage];

  return (
    <>
      <Modal
        title={title[manage]}
        isFullPage={true}
        isVisible={!!content[manage]}
        primaryAction={primary}
        secondaryActions={secondary}
        onCancel={onClose[manage]}
        cancelText={cancelText}
        disableFooter={disableFooter}
      >
        <Layout maxW="1000px" p={4} alignSelf="center" justify="center">
          <Content />
        </Layout>
      </Modal>
      {cancelGuidedAIWarning}
      {retrainGuidedAIWarning}
    </>
  );
};

export default AutomationHubManagement;
